import React from "react";
import dayjs from "dayjs";
import { IColumn, Icon, Link, TooltipHost, TooltipOverflowMode } from "@fluentui/react";
import { getFileTypeIconProps } from "@fluentui/react-file-type-icons";
import { detailRowLinkStyles } from "../../../src/taskpane/config/controlStyles";
import { IAggregatedColumn, getBindingForRow } from "../../../utilities/AggregatedColumnHelper";
import { logger } from "../../../utilities/logger";
import {
    renderBooleanCell,
    renderChoiceCell,
    renderDateCell,
    renderLookupCell,
    renderMultiChoiceCell,
    renderNoteCell,
    renderTaxonomyCell,
    renderTaxonomyMultiCell,
    renderTextCell,
    renderUserCell,
} from "./sharedFieldRenderers";

/**
 * Column factory for the cross-library "All documents" grid.
 *
 * Rows come from RenderListDataAsStream and are keyed by their SOURCE library's
 * internal field names, so every cell resolves its value through the merged column's
 * per-list binding (`item.__sourceListId` → binding.internalName) and renders with the
 * binding's own type. A display-name merge therefore never renders a value with the
 * wrong type, even when libraries disagree about the type behind a shared label.
 */

export {
    AGGREGATE_SOURCE_LIST_ID,
    AGGREGATE_SOURCE_KEY,
    AGGREGATE_SOURCE_LIBRARY_NAME,
    AGGREGATE_SOURCE_WEB_URL,
    AGGREGATE_SOURCE_SITE_NAME,
    AGGREGATE_ROW_KEY,
    AGGREGATE_SORT_VALUE,
} from "./aggregateRowFields";
import {
    AGGREGATE_SOURCE_LIST_ID,
    AGGREGATE_SOURCE_LIBRARY_NAME,
    AGGREGATE_SOURCE_SITE_NAME,
    AGGREGATE_ROW_KEY,
    AGGREGATE_SORT_VALUE,
} from "./aggregateRowFields";

const formatUtcDate = (value: string, format: string): string => {
    try {
        return (dayjs as any).utc ? (dayjs as any).utc(value).local().format(format) : dayjs(value).format(format);
    } catch (error) {
        logger(error, "aggregateColumnRenderers.tsx (formatUtcDate)");
        return value ?? "";
    }
};

/**
 * Renders one merged-column cell using the row's source-library binding, delegating to
 * the shared per-type renderers (the same implementations the single-library grid
 * uses). CustomFormatter/pill options are not passed because the aggregate schema
 * query does not fetch CustomFormatter — cells render in the plain style.
 */
export const renderAggregatedCell = (column: IAggregatedColumn, item: any): JSX.Element => {
    try {
        const binding = getBindingForRow(column, item?.[AGGREGATE_SOURCE_LIST_ID]);
        if (!binding) {
            return <></>; // column doesn't exist in this row's library
        }

        const value = item[binding.internalName];

        switch (binding.type) {
            case "MultiChoice":
                return renderMultiChoiceCell(value);

            case "Choice":
                return renderChoiceCell(value);

            case "Lookup":
            case "LookupMulti":
                return renderLookupCell(value);

            case "TaxonomyFieldType":
                return renderTaxonomyCell(value);

            case "TaxonomyFieldTypeMulti":
                return renderTaxonomyMultiCell(value);

            case "User":
            case "UserMulti":
                return renderUserCell(value);

            case "DateTime":
                return renderDateCell(value, binding.format === "DateOnly" ? "L" : "L LT", formatUtcDate);

            case "Calculated":
                if (binding.resultType === "DateOnly" || binding.resultType === "DateTime") {
                    return renderDateCell(value, binding.format === "DateOnly" ? "L" : "L LT", formatUtcDate);
                }
                return renderTextCell(value);

            case "Note":
                return renderNoteCell(value);

            case "Boolean":
                return renderBooleanCell(item[`${binding.internalName}.value`], column.displayName?.length ?? 5);

            case "URL": {
                const description = item[`${binding.internalName}.desc`];
                return value
                    ? <Link
                        data-selection-disabled={true}
                        rel="noopener"
                        styles={detailRowLinkStyles}
                        onClick={() => window.open(value, "_blank")}
                    >
                        {description || value}
                    </Link>
                    : <></>;
            }

            default:
                return renderTextCell(value);
        }
    } catch (error) {
        logger(error, "aggregateColumnRenderers.tsx (renderAggregatedCell)");
        return <></>;
    }
};

export interface IAggregatedGridColumnOptions {
    mergedColumns: IAggregatedColumn[];
    onOpenItem: (item: any) => void;
    // Receives the ROW (not a list id): a source is resolved by its source key, since
    // two sources can share a list (Tess folder dossiers).
    onOpenLibrary: (item: any) => void;
    headers: {
        name: string;
        library: string;
        modified: string;
        modifiedBy: string;
        // Given only when client-group sites take part: adds the Site column.
        site?: string;
    };
    // Optional cap on merged metadata columns (for a future column picker). When
    // omitted, ALL merged columns render — the grid scrolls horizontally by design.
    maxCustomColumns?: number;
}

export const buildAggregatedGridColumns = (options: IAggregatedGridColumnOptions): IColumn[] => {
    const columns: IColumn[] = [
        {
            key: "aggDocIcon",
            name: " ",
            isIconOnly: true,
            minWidth: 20,
            maxWidth: 20,
            onRender: (item: any) => (
                <Icon {...getFileTypeIconProps({ extension: item.File_x0020_Type, size: 20, imageFileType: "png" })} />
            ),
        },
        {
            key: "aggName",
            name: options.headers.name,
            fieldName: "FileLeafRef",
            minWidth: 180,
            maxWidth: 280,
            isRowHeader: true,
            isResizable: true,
            onRender: (item: any) => (
                <Link
                    data-selection-disabled={true}
                    rel="noopener"
                    styles={detailRowLinkStyles}
                    onClick={() => options.onOpenItem(item)}
                >
                    <TooltipHost content={item.FileLeafRef} overflowMode={TooltipOverflowMode.Parent}>
                        <span aria-describedby={item[AGGREGATE_ROW_KEY]}>{item.FileLeafRef}</span>
                    </TooltipHost>
                </Link>
            ),
        },
        // The row's site, just before its library, when the view spans client-group sites.
        ...(options.headers.site ? [{
            key: "aggSite",
            name: options.headers.site,
            fieldName: AGGREGATE_SOURCE_SITE_NAME,
            minWidth: 110,
            maxWidth: 180,
            isResizable: true,
            onRender: (item: any) => (
                <TooltipHost content={item[AGGREGATE_SOURCE_SITE_NAME]} overflowMode={TooltipOverflowMode.Parent}>
                    <span>{item[AGGREGATE_SOURCE_SITE_NAME]}</span>
                </TooltipHost>
            ),
        }] : []),
        {
            key: "aggLibrary",
            name: options.headers.library,
            fieldName: AGGREGATE_SOURCE_LIBRARY_NAME,
            minWidth: 110,
            maxWidth: 160,
            isResizable: true,
            onRender: (item: any) => (
                <Link
                    data-selection-disabled={true}
                    rel="noopener"
                    styles={detailRowLinkStyles}
                    onClick={() => options.onOpenLibrary(item)}
                >
                    <TooltipHost content={item[AGGREGATE_SOURCE_LIBRARY_NAME]} overflowMode={TooltipOverflowMode.Parent}>
                        <span>{item[AGGREGATE_SOURCE_LIBRARY_NAME]}</span>
                    </TooltipHost>
                </Link>
            ),
        },
        {
            key: "aggModified",
            name: options.headers.modified,
            fieldName: AGGREGATE_SORT_VALUE,
            minWidth: 90,
            maxWidth: 130,
            isResizable: true,
            isSorted: true,
            isSortedDescending: true,
            onRender: (item: any) => (item.Modified ? <span>{formatUtcDate(item.Modified, "L LT")}</span> : <></>),
        },
        {
            key: "aggEditor",
            name: options.headers.modifiedBy,
            minWidth: 100,
            maxWidth: 150,
            isResizable: true,
            onRender: (item: any) => renderTextCell(Array.isArray(item.Editor) ? item.Editor[0]?.title : undefined),
        },
    ];

    const mergedSource = options.maxCustomColumns
        ? (options.mergedColumns ?? []).slice(0, options.maxCustomColumns)
        : (options.mergedColumns ?? []);

    const customColumns = mergedSource
        .map((mergedColumn): IColumn => ({
            key: `agg_${mergedColumn.key}`,
            name: mergedColumn.displayName,
            minWidth: 110,
            maxWidth: 160,
            isResizable: true,
            data: mergedColumn,
            onRender: (item: any) => renderAggregatedCell(mergedColumn, item),
        }));

    return [...columns, ...customColumns];
};
